import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { getConsent } from "@/components/CookieBanner";

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

function loadGa4(ga4Id) {
  if (window.__ga4Loaded || !ga4Id) return;
  window.__ga4Loaded = true;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  // Consent Mode v2 — default denied until the user opts in.
  gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
  const s = document.createElement("script");
  s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
  document.head.appendChild(s);
  gtag("js", new Date());
  gtag("config", ga4Id, { anonymize_ip: true });
}

function applyConsent() {
  const c = getConsent();
  if (window.gtag && c) {
    window.gtag("consent", "update", {
      analytics_storage: c.analytics ? "granted" : "denied",
      ad_storage: c.marketing ? "granted" : "denied",
      ad_user_data: c.marketing ? "granted" : "denied",
      ad_personalization: c.marketing ? "granted" : "denied",
    });
  }
}

// Account, dashboard, checkout and one-off utility screens are kept out of search results.
const PRIVATE_PREFIXES = [
  "/admin", "/pos", "/dashboard", "/enhancer", "/studio", "/manager", "/council", "/coach", "/guide-coach",
  "/messages", "/settings", "/privacy-center", "/orders", "/my-learning", "/my-calendar", "/crm", "/checkout",
  "/payment", "/login", "/register", "/set-password", "/forgot-password", "/redeem", "/confirm",
  "/gift-certificate", "/manage-booking", "/ticket", "/f/", "/feedback-survey", "/data-request",
  "/gauge/app", "/gauge/journey", "/community/members", "/community/groups", "/connect-demo",
];
export const isPrivatePath = (p) =>
  PRIVATE_PREFIXES.some((x) => p === x || p.startsWith(x.endsWith("/") ? x : `${x}/`)) || /^\/courses\/[^/]+\/certificate/.test(p) || /^\/events\/[^/]+\/(watch|studio)/.test(p);

function RouteRobots() {
  const { pathname } = useLocation();
  useEffect(() => {
    const existing = document.head.querySelector('meta[name="robots"][data-route-robots]');
    if (isPrivatePath(pathname)) {
      const el = existing || Object.assign(document.createElement("meta"), { name: "robots" });
      el.setAttribute("data-route-robots", "1");
      el.content = "noindex, nofollow";
      if (!existing) document.head.appendChild(el);
    } else if (existing) {
      existing.remove();
    }
  }, [pathname]);
  return null;
}

export default function SiteSEO() {
  const { data: seo } = useQuery({ queryKey: ["seo-settings"], queryFn: () => api.get("/seo/settings").then((r) => r.data), staleTime: 600000 });

  useEffect(() => {
    if (!seo) return;
    const origin = window.location.origin;
    injectJsonLd("ld-org", {
      "@context": "https://schema.org", "@type": "Organization",
      name: seo.org_name || "Enhancing Your World", url: origin,
      logo: seo.org_logo || undefined,
      sameAs: [seo.social_instagram, seo.social_facebook, seo.social_youtube, seo.google_business_url].filter(Boolean),
    });
    injectJsonLd("ld-website", {
      "@context": "https://schema.org", "@type": "WebSite",
      name: seo.org_name || "Enhancing Your World", url: origin,
    });
    loadGa4(seo.ga4_id);
    applyConsent();
    const h = () => applyConsent();
    window.addEventListener("eyw-consent", h);
    return () => window.removeEventListener("eyw-consent", h);
  }, [seo]);

  return <RouteRobots />;
}
